import AddButton from "app/components/AddButton";
import { DropdownIcon } from "app/components/Icons";
import CommonGridTable from "app/components/Table/CommonGridTable";
import AddTaskModal from "app/containers/Features/containers/Boards/components/AddTaskModalComponent/AddTaskModal";
import { ticketGroupsActions } from "app/containers/Features/containers/Boards/containers/Tickets/redux/slice";
import MyTicketsRow from "app/containers/Features/containers/Home/components/MyTicketsRow";
import TicketFilters from "app/containers/Features/containers/Home/components/TicketFilters/index";
import { selectTicketTypes } from "app/redux/selector";
import { sortOrder } from "app/types";
import React, { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { findDefaultBoardTicketType } from "../../Boards/helpers";
import { myTicketsHeaders } from "../data";
import { useMyTicketsTable } from "../hooks/useMyTicketsFilter";
import {
  selectMyTicketsList,
  selectTicketsPagination,
} from "../redux/selector";
import { actions } from "../redux/slice";

const MyTicketsTable = () => {
  const [showModal, setShowModal] = useState(false);
  const gridTemplateColumns =
    "746px minmax(100px, 1fr) minmax(110px, 1fr) minmax(120px, 1fr) minmax(80px, 1fr) minmax(80px, 1fr)";

  const {
    isFiltersVisible,
    toggleFiltersVisibility,
    hasMore,
    isLoading,
    onLoadMore,
  } = useMyTicketsTable();

  const dispatch = useDispatch();
  const myTicketsData = useSelector(selectMyTicketsList);
  const pagination = useSelector(selectTicketsPagination);
  const ticketTypes = useSelector(selectTicketTypes);
  const isInitialTicketsLoading = isLoading && pagination.page === 1;
  const isEmptyState = !isInitialTicketsLoading && myTicketsData.length === 0;

  const handleSort = useCallback(
    (columnId: string) => {
      const isSameColumn = pagination.orderBy === columnId;
      const newOrder =
        isSameColumn && pagination.orderDirection === sortOrder.DESC
          ? sortOrder.ASC
          : sortOrder.DESC;

      dispatch(actions.setTicketsOrderBy(columnId));
      dispatch(actions.setTicketsOrderDirection(newOrder));
      dispatch(
        actions.setTicketsPagination({
          ...pagination,
          orderBy: columnId,
          orderDirection: newOrder,
          page: 1,
        }),
      );
      dispatch(actions.setMyTicketsList([])); // Clear list to reset
      dispatch(actions.getMyTickets());
    },
    [dispatch, pagination],
  );

  const type = useMemo(
    () => findDefaultBoardTicketType(ticketTypes || []),
    [ticketTypes],
  );

  const showCreateTicketModal = () => {
    dispatch(ticketGroupsActions.clearCreateTicket());
    dispatch(
      ticketGroupsActions.setTicketFormValue({ name: "type", value: type }),
    );
    setShowModal(true);
  };
  const onClose = () => {
    dispatch(actions.getChartData());
    dispatch(actions.getMyTickets());
    setShowModal(false);
  };

  return (
    <div className="flex w-full flex-1 min-h-0 flex-col rounded-md border border-gray-100 bg-white">
      <div className="mb-3 flex flex-col items-start gap-y-1 px-6 pt-6">
        <div className="flex w-full items-center justify-between">
          <h2 className="text-sm font-medium leading-5 text-black">
            My tickets ({myTicketsData.length})
          </h2>
          <div className="cursor-pointer" onClick={toggleFiltersVisibility}>
            <div
              className={`transform transition-transform duration-200 ${
                isFiltersVisible ? "rotate-180" : ""
              }`}
            >
              <DropdownIcon size={20} />
            </div>
          </div>
        </div>

        {isFiltersVisible && (
          <div className="mt-2 mb-2">
            <TicketFilters />
          </div>
        )}
      </div>

      <CommonGridTable
        headers={myTicketsHeaders}
        gridTemplateColumns={gridTemplateColumns}
        className={`${isEmptyState ? "min-h-0" : "flex-1 min-h-0"} scrollbar-none [&>div:first-child]:!bg-white`}
        emptyText=""
        headerClassName="!h-[30px] !min-h-[30px] !border-t-0 !border-x-0 !rounded-none border-b border-gray-100 text-[#6B7280] bg-[#EEF0F5]"
        headerTitleClassName="text-xs font-normal text-secondary"
        padding="px-4"
        isLoading={isLoading}
        hasMore={hasMore}
        onLoadMore={onLoadMore}
        orderBy={pagination.orderBy}
        orderDir={pagination.orderDirection}
        onSort={handleSort}
      >
        {isEmptyState && (
          <div className="flex h-[52px] items-center justify-center border-b border-gray-100 bg-white">
            <span className="text-sm text-secondary">
              There are no any tickets yet
            </span>
          </div>
        )}

        {!isInitialTicketsLoading &&
          myTicketsData.map((row) => (
            <MyTicketsRow
              key={row._id}
              row={row}
              gridTemplateColumns={gridTemplateColumns}
            />
          ))}
      </CommonGridTable>

      <div className="pl-4 pb-2">
        <AddButton label="Add Ticket" onClick={showCreateTicketModal} />
      </div>

      {showModal && (
        <AddTaskModal
          onClose={onClose}
          title="Add Ticket"
          createTicketForHome
        />
      )}
    </div>
  );
};

export default MyTicketsTable;
